import React from "react";
import TransactionItem from "components/transaction-item/TransactionItem";
import FilterChips, { FilterOption } from "components/filter-chips/FilterChips";
import TransactionDetailModal from "modals/transaction-detail/TransactionDetailModal";
import QrModal from "modals/qr-modal/QrModal";
import BalancePanel from "components/balance-panel/BalancePanel";
import KeaValleyCardV2 from "components/kea-valley-card/KeaValleyCardV2";
import {
  SkeletonCard,
  SkeletonBalancePanel,
  SkeletonTransactionItem,
} from "components/skeleton";
import { useUserPage, TxFilter } from "pages/user-page/useUserPage";
import "./user-page.css";
import { useSelector } from "react-redux";
import {
  cardInfoSelector,
  cardNumberSelector,
  networkTypeSelector,
} from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { tokenHoldingsUrl } from "utils/tokens";

const TX_FILTER_OPTIONS: FilterOption<TxFilter>[] = [
  { value: "all", label: "All" },
  { value: "in", label: "In" },
  { value: "out", label: "Out" },
];

const TX_SKELETON_COUNT = 3;

function UserPage() {
  const {
    // cardNumber,
    // cardData,
    cardBalance,
    network,
    // showLoadMore,
    isCardLoading,
    isCardMissing,
    isBalanceLoading,
    isTxLoading,
    txFilter,
    setTxFilter,
    filteredTransactions,
    currentTransaction,
    showTransactionDetail,
    openTransactionDetail,
    closeTransactionDetail,
    showQrCode,
    openQrCode,
    closeQrCode,
    copied,
    handleCopy,
  } = useUserPage();

  // Only the cardInfo load blanks the whole panel — at that point nothing about
  // the card is known. Once it resolves, the panel renders with its status and
  // address while BalancePanel's own skeleton covers the balance still in
  // flight. Gating the panel on isBalanceLoading too would make that inner
  // skeleton unreachable.
  const showPanelSkeleton = isCardLoading;
  const cardNumberFromStore = useSelector(cardNumberSelector);
  const cardInfoFromStore = useSelector(cardInfoSelector);
  const networkType = useSelector(networkTypeSelector);
  const { selected: selectedToken, label: tokenSymbol } = useSettlementToken();

  // Card art renders its •••• placeholder on an empty string, so a card that
  // doesn't exist on this network shows the blank card rather than a number
  // implying it's here.
  const displayedCardNumber = isCardMissing ? "" : (cardNumberFromStore ?? "");
  const cardAlias = isCardMissing ? "" : (cardInfoFromStore?.cardAlias ?? "");

  return (
    <>
      {
        <main className="gp-v1-grid">
          <div className="gp-v1-card-stack">
            {isCardLoading ? (
              <SkeletonCard />
            ) : (
              <KeaValleyCardV2 cardNumber={displayedCardNumber} />
            )}

            {showPanelSkeleton ? (
              <SkeletonBalancePanel />
            ) : (
              <BalancePanel
                balance={cardBalance}
                isBalanceLoading={isBalanceLoading}
                cardStatus={cardInfoFromStore?.cardStatus}
                cardAlias={cardAlias}
                cardType={cardInfoFromStore?.cardType}
                network={network}
                tokenSymbol={tokenSymbol}
                isCardMissing={isCardMissing}
                copied={copied}
                onCopy={handleCopy}
                onOpenExplorer={() =>
                  window.open(
                    tokenHoldingsUrl(
                      networkType,
                      selectedToken?.address,
                      cardAlias,
                    ),
                    "_blank",
                  )
                }
                onOpenQr={openQrCode}
              />
            )}
          </div>

          {/* RIGHT: Transactions */}
          <div className="gp-v1-tx-section">
            <div className="gp-v1-tx-header">
              <div className="gp-v1-tx-title">Transactions</div>
              <div className="gp-v1-tx-tools">
                <FilterChips
                  options={TX_FILTER_OPTIONS}
                  value={txFilter}
                  onChange={setTxFilter}
                />
              </div>
            </div>

            <div className="gp-v1-tx-list">
              {isTxLoading ? (
                Array.from({ length: TX_SKELETON_COUNT }, (_, i) => (
                  <SkeletonTransactionItem key={i} />
                ))
              ) : filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <TransactionItem
                    key={transaction.txId}
                    transaction={transaction}
                    tokenSymbol={tokenSymbol}
                    openTransactionDetail={openTransactionDetail}
                  />
                ))
              ) : (
                <div className="gp-v1-empty">
                  {isCardMissing
                    ? `No card found on ${network?.label ?? "this network"}.`
                    : "No transactions found."}
                </div>
              )}
            </div>
          </div>
        </main>
      }

      {showTransactionDetail && (
        <TransactionDetailModal
          isOpen={showTransactionDetail}
          handleClose={closeTransactionDetail}
          transaction={currentTransaction}
          tokenSymbol={tokenSymbol}
        />
      )}

      {showQrCode && (
        <QrModal
          isOpen={showQrCode}
          data={cardAlias}
          close={closeQrCode}
          warning={`Please make sure the amount you send is in ${tokenSymbol} — otherwise the funds will be lost.`}
        />
      )}
    </>
  );
}

export default UserPage;
